import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * TriCheckbox — on / off / mixed / locked checkbox (T-0140).
 *
 * Mirrors `.cbx` in `docs/requirement-library/prototype/selection.css`:
 * 16px box, ink-900 fill when on or mixed, a dash for mixed, and a muted
 * ink-200 fill with a check for locked (the row is already included and
 * cannot be changed here).
 *
 * A real `<button role="checkbox">` so Space toggles it and it sits in
 * the tab order. `aria-checked` is `true`, `false` or `"mixed"`; locked
 * reports `aria-checked="true"` plus `aria-disabled`.
 *
 * `onChange` receives the mouse or keyboard event so a grid can read
 * `shiftKey` for range selection.
 */
export type TriCheckboxState = 'on' | 'off' | 'mixed' | 'locked';

export interface TriCheckboxProps {
  state: TriCheckboxState;
  /** Called with the next boolean value. Never called when locked. */
  onChange?: (next: boolean, event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Accessible name. Required because the box has no visible label. */
  'aria-label': string;
  /** Remove from the tab order (for example inside a grid row). */
  tabIndex?: number;
  /** Shown but not selectable yet (T-0187): an unchecked, dimmed box that ignores clicks. */
  disabled?: boolean;
  title?: string;
  className?: string;
}

export function TriCheckbox({
  state,
  onChange,
  'aria-label': ariaLabel,
  tabIndex,
  disabled = false,
  title,
  className,
}: TriCheckboxProps) {
  const locked = state === 'locked';
  const ariaChecked = state === 'mixed' ? 'mixed' : state === 'on' || locked;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={ariaChecked}
      aria-disabled={locked || disabled || undefined}
      aria-label={ariaLabel}
      title={title}
      tabIndex={tabIndex}
      data-state={state}
      onClick={(e) => {
        // Keep row-level click handlers (open, focus) from also firing.
        e.stopPropagation();
        if (locked || disabled) return;
        // Mixed resolves to "on", matching select-all behaviour.
        onChange?.(state !== 'on', e);
      }}
      className={cn(
        'inline-flex h-4 w-4 flex-none items-center justify-center rounded-sm border-[1.5px] align-middle text-paper-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
        state === 'off' && 'border-ink-300 bg-paper-0 hover:border-ink-500',
        (state === 'on' || state === 'mixed') && 'border-ink-900 bg-ink-900',
        locked && 'cursor-default border-ink-200 bg-ink-200',
        disabled && 'cursor-not-allowed opacity-40 hover:border-ink-300',
        className,
      )}
    >
      {state === 'on' || locked ? (
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
      ) : null}
      {state === 'mixed' ? (
        <span className="h-0.5 w-2 rounded-full bg-paper-0" aria-hidden="true" />
      ) : null}
    </button>
  );
}

TriCheckbox.displayName = 'TriCheckbox';

/**
 * The box of a `TriCheckbox` without the control (T-0186): a ticked or
 * empty square beside text that is a record, not a choice (an interview's
 * questions once held). Hidden from assistive technology; say it in text.
 */
export function CheckboxMark({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-4 w-4 flex-none items-center justify-center rounded-sm border-[1.5px] align-middle text-paper-0',
        on ? 'border-ink-900 bg-ink-900' : 'border-ink-300 bg-paper-0',
      )}
    >
      {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
    </span>
  );
}
